import React from 'react';
import {
    Box,
    Typography,
    Switch,
    Card,
    Chip,
    Tooltip,
    alpha,
    Stack,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import LockClockOutlinedIcon from '@mui/icons-material/LockClockOutlined';

interface ConsentToggleProps {
    label: 'Email' | 'SMS' | string;
    checked: boolean;
    onChange: () => void;
    pending?: boolean;
}

const ConsentToggle: React.FC<ConsentToggleProps> = ({ label, checked, onChange, pending }) => {
    const isEmail = label.toLowerCase() === 'email';
    const Icon = isEmail ? EmailOutlinedIcon : SmsOutlinedIcon;

    const status = pending ? 'Pending' : checked ? 'Opted-In' : 'Opted-Out';
    const statusColor = pending ? 'warning' : checked ? 'success' : 'default';

    return (
        <Card
            elevation={0}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2.5,
                py: 2,
                mb: 2,
                borderRadius: 3,
                border: (t) => `1px solid ${alpha(t.palette.divider, 0.6)}`,
                background: (t) => alpha(t.palette.background.paper, 0.7),
                backdropFilter: 'blur(6px)',
                transition: 'box-shadow 0.2s ease, transform 0.1s ease',
                '&:hover': { boxShadow: 2 },
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        display: 'grid',
                        placeItems: 'center',
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.6)}`,
                        background: (t) => alpha(t.palette.primary.main, 0.06),
                    }}
                >
                    <Icon fontSize="small" />
                </Box>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Control marketing {isEmail ? 'emails' : 'text messages'}
                    </Typography>
                </Box>
            </Stack>

            <Stack direction="row" spacing={1.25} alignItems="center">
                <Chip
                    size="small"
                    color={statusColor}
                    variant={statusColor === 'default' ? 'outlined' : 'filled'}
                    label={status}
                    icon={pending ? <LockClockOutlinedIcon /> : undefined}
                    sx={{ minWidth: 92, fontWeight: 600 }}
                />
                <Tooltip
                    title={pending ? 'Pending verification—changes disabled' : ''}
                    arrow
                    disableHoverListener={!pending}
                >
                    <span>
                        <Switch
                            color="primary"
                            checked={checked}
                            onChange={onChange}
                            disabled={!!pending}
                            inputProps={{ 'aria-label': `${label} consent toggle` }}
                            sx={{
                                '& .MuiSwitch-track': {
                                    opacity: 1,
                                },
                            }}
                        />
                    </span>
                </Tooltip>
            </Stack>
        </Card>
    );
};

export default ConsentToggle;